import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import AccountContext from '../../contexts/AccountContext';
import ScorecardContext from 'contexts/ScorecardContext';
import ScorecardsContext from '../../contexts/ScorecardsContext';
import updateScorecardDoc from '../../screens/ScorecardsIndex/updateScorecardDoc';
import AccountPickerContext from '../../contexts/AccountPickerContext';
import { convertWeekDateToIsoDate } from '../../types/scorecardDates/convertDates';
import isIsoDate from '../../types/scorecardDates/isIsoDate';
import GqlClientProvider from '../SplashScreenProviders/UserAndAccountProviders/GqlClientProvider';
import WeekStartsOnOverrideContext from '../../contexts/WeekStartsOnOverrideContext';
import ErrorBoundary from '../../components/Common/ErrorBoundary';
import DashboardContext from '../../contexts/DashboardContext';
import useScorecardData from './useScorecardData';
import useQuickFilters from 'hooks/useQuickFilters';
import useScorecardQuickFilterCompatibility from '../../screens/ScorecardShow/useScorecardQuickFilterCompatibility';
import Loading from 'components/Loading/Loading';
import { AccountTodayOverrideContext } from '../SplashScreenProviders/UserAndAccountProviders/AccountTodayOverrideProvider';
import { FiscalPeriodsContext } from '../SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider';
import buildScorecardPeriods from './buildScorecardPeriods';
import CurrentUserContext from 'contexts/CurrentUserContext';
import getTimeStamp from 'getTimeStamp';

interface ScorecardProviderGateProps {
  children: JSX.Element | JSX.Element[];
  scorecardId: string;
  isAddKpiEnabled?: boolean;
  isDragAndDropEnabled?: boolean;
  isKpisDisabled?: boolean;
  isMenuEnabled?: boolean;
  // Past periods shown, up to and including the current one. Absent is 3 on
  // a dashboard, otherwise the KPI List's own default.
  numberOfPeriods?: number;
  // Where it's drawn, for when DashboardContext can't tell (Studio doesn't
  // provide it). Absent reads DashboardContext.
  contentType?: 'dashboard' | 'report';
}

interface ScorecardProviderProps extends ScorecardProviderGateProps {
  scorecard: Scorecards.Scorecard;
  setScorecard: React.Dispatch<
    React.SetStateAction<Scorecards.Scorecard | undefined>
  >;
}

const useScorecardPeriods = (
  toIsoDate: (date: Scorecards.WeekDate | Scorecards.IsoDate) => string,
  scorecard: Scorecards.Scorecard,
) => {
  const { weekStartsOn } = useContext(AccountContext);
  const { todayOverride } = useContext(AccountTodayOverrideContext);
  const { fiscalPeriods, currentFiscalPeriod } =
    useContext(FiscalPeriodsContext);

  return useMemo(
    () =>
      buildScorecardPeriods({
        scorecard,
        now: moment.utc(todayOverride).startOf('day').format('YYYY-MM-DD'),
        toIsoDate,
        weekStartsOn,
        fiscalPeriods,
        currentFiscalPeriod,
      }),
    [
      scorecard,
      todayOverride,
      toIsoDate,
      weekStartsOn,
      fiscalPeriods,
      currentFiscalPeriod,
    ],
  );
};

const ScorecardProvider = ({
  children,
  scorecard,
  setScorecard,
  isAddKpiEnabled,
  isDragAndDropEnabled,
  isKpisDisabled,
  isMenuEnabled,
  numberOfPeriods,
  contentType,
}: ScorecardProviderProps) => {
  // Context
  const { weekStartsOn } = useContext(AccountContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { dashboard } = useContext(DashboardContext);
  const isOnDashboard = contentType
    ? contentType === 'dashboard'
    : dashboard !== undefined;
  const currentUser = useContext(CurrentUserContext);

  // State
  const [poppedUpKpi, setPoppedUpKpi] = useState<
    Scorecards.ScorecardKpi | Scorecards.ManualKpiRow
  >();
  const [selectedPeriods, setSelectedPeriods] = useState<Period[]>([]);
  const [manualKpiIdBeingEdited, setManualKpiIdBeingEdited] =
    useState<string>();
  const kpisListDivRef = useRef<HTMLDivElement>(null);
  const kpisListDivParentScrollerRef = useRef<HTMLDivElement>(null);
  const [kpiBeingHighlighted, setKpiBeingHighlighted] = useState<
    string | undefined
  >();
  const [hoveredKpiId, setHoveredKpiId] = useState<string | undefined>();
  const [selectedQuickFilters, setSelectedQuickFilters] = useState<
    LegacySavedFilter[]
  >([]);

  // Quick filters
  const quickFilters = useQuickFilters(`scorecard: ${scorecard.id}`);
  const getQuickFilterCompatibility = useScorecardQuickFilterCompatibility({
    scorecard,
  });

  // Callbacks
  const toIsoDate = useCallback(
    (date: Scorecards.IsoDate | Scorecards.WeekDate) => {
      if (isIsoDate(date)) {
        return date;
      }

      const startOfWeekToUse = scorecard.weekStartsOnOverride
        ? scorecard.weekStartsOnOverride
        : weekStartsOn;

      return convertWeekDateToIsoDate(date, startOfWeekToUse);
    },
    [scorecard, weekStartsOn],
  );

  const updateScorecard = useCallback(
    async (updatedScorecard: Scorecards.Scorecard) => {
      const stampedScorecard: Scorecards.Scorecard = {
        ...updatedScorecard,
        updatedBy: currentUser.id,
        updatedOn: getTimeStamp(),
      };
      setScorecard(stampedScorecard);
      await updateScorecardDoc(stampedScorecard, selectedAccountId);
    },
    [currentUser.id, selectedAccountId, setScorecard],
  );

  const highlightKpi = useCallback((kpiId: string) => {
    setKpiBeingHighlighted(kpiId);
    setTimeout(() => {
      setKpiBeingHighlighted(undefined);
    }, 2000);
  }, []);

  // Hooks
  const { periods, pastPeriods, futurePeriods } = useScorecardPeriods(
    toIsoDate,
    scorecard,
  );
  const { data, isLoading } = useScorecardData({
    scorecard,
    selectedPeriods,
    futurePeriods,
    isKpisDisabled,
    selectedQuickFilters,
  });

  // Effects
  // Drop any selected quick filter that has become incompatible with the
  // scorecard's current datasets (e.g. after a KPI edit) so we never apply a
  // filter whose pill is disabled.
  useEffect(() => {
    setSelectedQuickFilters((prev) =>
      prev.filter((f) => getQuickFilterCompatibility(f).isCompatible),
    );
  }, [getQuickFilterCompatibility]);

  // Future periods are always shown on the KPI List itself, but dashboards
  // only show the last periods up to the current one (3 unless the card says
  // otherwise).
  useEffect(() => {
    if (isOnDashboard) {
      setSelectedPeriods(pastPeriods.slice(-(numberOfPeriods ?? 3)));
      return;
    }

    setSelectedPeriods([
      ...pastPeriods.slice(
        -(numberOfPeriods ?? (scorecard.defaultSelectedNumberOfPeriods || 12)),
      ),
      ...futurePeriods,
    ]);
  }, [
    futurePeriods,
    isOnDashboard,
    numberOfPeriods,
    pastPeriods,
    scorecard.defaultSelectedNumberOfPeriods,
  ]);

  if (!scorecard) {
    return null;
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <ScorecardContext.Provider
      value={{
        scorecard,
        periods,
        pastPeriods,
        futurePeriods,
        selectedPeriods,
        setSelectedPeriods,
        updateScorecard,
        isLoading,
        data,
        poppedUpKpi,
        setPoppedUpKpi,
        isAddKpiEnabled,
        isDragAndDropEnabled,
        isKpisDisabled,
        isMenuEnabled,
        isOnDashboard,
        toIsoDate,
        kpisListDivRef,
        kpisListDivParentScrollerRef,
        manualKpiIdBeingEdited,
        setManualKpiIdBeingEdited,
        highlightKpi,
        kpiBeingHighlighted,
        hoveredKpiId,
        setHoveredKpiId,
        quickFilters,
        selectedQuickFilters,
        setSelectedQuickFilters,
      }}
    >
      {children}
    </ScorecardContext.Provider>
  );
};

const Gate = (props: ScorecardProviderGateProps) => {
  const { scorecards } = useContext(ScorecardsContext);

  const [scorecard, setScorecard] = useState<
    Scorecards.Scorecard | undefined
  >();

  useEffect(() => {
    const newScorecard = scorecards.find((g) => g.id === props.scorecardId);
    setScorecard(newScorecard);
  }, [props.scorecardId, scorecards]);

  if (!scorecard) {
    return null;
  }

  return (
    <ErrorBoundary>
      <WeekStartsOnOverrideContext.Provider
        value={{
          weekStartsOnOverride: scorecard
            ? scorecard.weekStartsOnOverride
            : undefined,
        }}
      >
        <GqlClientProvider
          visId={scorecard.id}
          visType={'scorecard'}
          visName={scorecard.title}
        >
          <ScorecardProvider
            {...props}
            scorecard={scorecard}
            setScorecard={setScorecard}
          />
        </GqlClientProvider>
      </WeekStartsOnOverrideContext.Provider>
    </ErrorBoundary>
  );
};

export default Gate;
